export const IMPERSONATION_STORAGE_KEY = "quizrun.impersonation";
export const READ_ONLY_BLOCKED_EVENT = "quizrun:read-only-blocked";

export interface ImpersonationState {
  targetName: string;
  expiresAt: number;
}

function storage(): Storage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

// A malformed record reads as already expired, so the normal expiry path signs
// out locally and clears it instead of leaving the guard on without a banner.
const EXPIRED_UNKNOWN: ImpersonationState = {
  targetName: "someone",
  expiresAt: 0,
};

export function readImpersonation(): ImpersonationState | null {
  const raw = storage()?.getItem(IMPERSONATION_STORAGE_KEY);
  if (raw == null) return null;
  try {
    const state = JSON.parse(raw) as Partial<ImpersonationState> | null;
    return typeof state?.targetName === "string" &&
      typeof state.expiresAt === "number"
      ? { targetName: state.targetName, expiresAt: state.expiresAt }
      : EXPIRED_UNKNOWN;
  } catch {
    return EXPIRED_UNKNOWN;
  }
}

export function hasImpersonationRecord(): boolean {
  return storage()?.getItem(IMPERSONATION_STORAGE_KEY) !== null;
}

export function isImpersonationExpired(
  state: ImpersonationState,
  now = Date.now(),
): boolean {
  return state.expiresAt <= now;
}

export function saveImpersonation(targetName: string): ImpersonationState {
  const state: ImpersonationState = {
    targetName,
    expiresAt: Date.now() + 15 * 60 * 1000,
  };
  const target = storage();
  if (!target) throw new Error("Browser storage is unavailable.");
  target.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function clearImpersonation(): void {
  storage()?.removeItem(IMPERSONATION_STORAGE_KEY);
}

export function notifyReadOnlyBlocked(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(READ_ONLY_BLOCKED_EVENT));
  }
}
